import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, Text, View } from "react-native";

import {
  findUserByEmail,
  listCampusAdmins,
  provisionCampusAdmin,
  removeCampusAdmin,
  type AdminCampusAdminRow,
} from "../../../lib/admin";
import { listUniversities, type University } from "../../../lib/universities";
import {
  AdminAvatar,
  AdminBanner,
  AdminButton,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
  ListRow,
  ListRowGroup,
} from "../../../components/admin/ui";
import { SectionHeader } from "../../../components/aspects/SectionHeader";

const THEME = PANE_THEMES.Admin;

export default function AdminCampusAdminsScreen() {
  const [rows, setRows] = useState<AdminCampusAdminRow[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [universityId, setUniversityId] = useState<string | null>(null);
  const [uniQuery, setUniQuery] = useState("");
  const [provisioning, setProvisioning] = useState(false);
  const [armedRemove, setArmedRemove] = useState<string | null>(null);
  const armedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const [admins, unis] = await Promise.all([listCampusAdmins(), listUniversities()]);
      setRows(admins);
      setUniversities(unis);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load campus admins");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const selectedUniversity = useMemo(
    () => universities.find((u) => u.id === universityId) ?? null,
    [universities, universityId]
  );

  const filteredUniversities = useMemo(() => {
    const q = uniQuery.trim().toLowerCase();
    if (!q) return universities.slice(0, 8);
    return universities.filter((u) => u.name.toLowerCase().includes(q)).slice(0, 8);
  }, [universities, uniQuery]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  useEffect(
    () => () => {
      if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
    },
    []
  );

  async function onProvision() {
    if (!email.trim() || !selectedUniversity) return;
    setErr(null);
    setToast(null);
    setProvisioning(true);
    try {
      const user = await findUserByEmail(email.trim());
      if (!user) {
        setErr("No user found with that email. Have them sign up first, then provision.");
        return;
      }
      await provisionCampusAdmin(user.id, selectedUniversity.name);
      setToast(`Provisioned ${user.full_name ?? email.trim()} as campus admin for ${selectedUniversity.name}. They'll see the campus portal on next sign-in.`);
      setEmail("");
      setUniversityId(null);
      setUniQuery("");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to provision campus admin");
    } finally {
      setProvisioning(false);
    }
  }

  async function onRemove(userId: string) {
    if (armedRemove !== userId) {
      setArmedRemove(userId);
      if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
      armedTimerRef.current = setTimeout(() => setArmedRemove(null), 4000);
      return;
    }
    if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
    setArmedRemove(null);
    try {
      await removeCampusAdmin(userId);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to remove campus admin");
    }
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 880 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Platform · Campus admins"
          title="Campus admins"
          subtitle="Per-university operators who can manage students from their school."
        />

        {err ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={err} onRetry={() => void load()} /></View> : null}
        {toast ? <View style={{ marginBottom: SPACING.md }}><AdminBanner theme={THEME} tone="success" message={toast} /></View> : null}

        {/* Provision form */}
        <View style={{ padding: 16, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border }}>
          <SectionHeader theme={THEME} title="Provision new campus admin" tone="dark" />
          <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim, marginBottom: 12 }}>
            User must already have a Coravu account — find them by email, then bind to a university. Promoting to campus_admin replaces any existing role for that user.
          </Text>

          <AdminInput theme={THEME} label="User email" value={email} onChangeText={setEmail} placeholder="user@example.edu" autoCapitalize="none" keyboardType="email-address" />

          <View style={{ marginTop: 14 }}>
            <Text style={labelStyle}>University</Text>
            {selectedUniversity ? (
              <View style={{ marginTop: 8, padding: 10, borderRadius: RADII.sm, borderWidth: STROKES.hairline, borderColor: THEME.accentBorderHover, backgroundColor: THEME.accentSoft, flexDirection: "row", alignItems: "center", gap: 10 }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 13, color: THEME.textOnSurface }}>{selectedUniversity.name}</Text>
                  {selectedUniversity.primary_email_domain ? (
                    <Text style={{ marginTop: 2, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>@{selectedUniversity.primary_email_domain}</Text>
                  ) : null}
                </View>
                <AdminButton theme={THEME} label="Change" variant="outline" size="sm" onPress={() => { setUniversityId(null); setUniQuery(""); }} />
              </View>
            ) : (
              <>
                <AdminInput theme={THEME} value={uniQuery} onChangeText={setUniQuery} placeholder="Search universities…" autoCapitalize="none" style={{ marginTop: 8 }} />
                {filteredUniversities.length === 0 && uniQuery.trim() ? (
                  <Text style={{ marginTop: 8, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>
                    No match. The university must exist in the universities table first.
                  </Text>
                ) : (
                  <View style={{ marginTop: 8 }}>
                    <ListRowGroup theme={THEME}>
                      {filteredUniversities.map((u, i) => (
                        <ListRow
                          key={u.id}
                          theme={THEME}
                          first={i === 0}
                          onPress={() => { setUniversityId(u.id); setUniQuery(""); }}
                          title={u.name}
                          subtitle={u.primary_email_domain ? `@${u.primary_email_domain}` : undefined}
                        />
                      ))}
                    </ListRowGroup>
                  </View>
                )}
              </>
            )}
          </View>

          <View style={{ marginTop: 16 }}>
            <AdminButton theme={THEME} label="Provision campus admin" loading={provisioning} disabled={!email.trim() || !selectedUniversity} onPress={() => void onProvision()} />
          </View>
        </View>

        <View style={{ marginTop: SPACING.lg }}>
          <SectionHeader theme={THEME} title="Existing" tone="dark" count={rows.length} />
          {loading ? (
            <AdminLoading theme={THEME} />
          ) : rows.length === 0 ? (
            <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>No campus admins provisioned yet.</Text>
          ) : (
            <ListRowGroup theme={THEME}>
              {rows.map((row, i) => {
                const armed = armedRemove === row.user_id;
                return (
                  <ListRow
                    key={row.user_id}
                    theme={THEME}
                    first={i === 0}
                    leading={<AdminAvatar theme={THEME} name={row.full_name} />}
                    title={row.full_name ?? "User"}
                    subtitle={
                      <View>
                        <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }} numberOfLines={1}>{row.email ?? "—"}</Text>
                        <Text style={{ marginTop: 1, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }} numberOfLines={1}>{row.university_name}</Text>
                      </View>
                    }
                    trailing={
                      <AdminButton theme={THEME} label={armed ? "Click again" : "Remove"} variant={armed ? "destructive" : "outline"} size="sm" onPress={() => void onRemove(row.user_id)} />
                    }
                  />
                );
              })}
            </ListRowGroup>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

const labelStyle = {
  fontFamily: THEME.bodySemiBoldFont,
  fontSize: 10,
  letterSpacing: 2.2,
  textTransform: "uppercase" as const,
  color: THEME.textOnSurfaceFaint,
};
